/**
 * web/src/hooks/mutations/usePurgeWorkflows.ts
 * Admin-only, non-production-only on the server. Deletes EVERY workflow so a
 * tester can re-trigger the same deals/speakers from a clean slate.
 *
 * Cache handling splits deliberately: per-workflow detail + activity entries
 * are REMOVED (their rows are gone — invalidating would refetch straight into
 * a 404), while the list/queue/roster/stats queries are INVALIDATED so they
 * refetch and render their empty states.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { qk } from '../../lib/queryKeys';
import { purgeWorkflows } from '../../api/purge';
import { toastApiError } from '../../lib/api';

/**
 * Purges all workflows and their approvals.
 *
 * @returns React Query mutation handle (mutate())
 * @sideEffects HTTP POST; drops every workflow-derived cache entry; fires a
 *   toast reporting what was removed. External systems are untouched.
 * @idempotent yes — a second run reports zeros.
 */
export function usePurgeWorkflows() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => purgeWorkflows(),
    onSuccess: (result) => {
      // Deleted rows, not stale rows — drop them rather than refetch a 404.
      qc.removeQueries({ queryKey: qk.workflow.allRoot() });
      qc.removeQueries({ queryKey: qk.activityRoot() });

      qc.invalidateQueries({ queryKey: qk.workflows.root() });
      qc.invalidateQueries({ queryKey: qk.gates.root() });
      // Speaker journeys/roster project live workflow state onto board rows.
      qc.invalidateQueries({ queryKey: qk.speakers.root() });
      qc.invalidateQueries({ queryKey: qk.stats.root() });
      qc.invalidateQueries({ queryKey: qk.trackedEmails.root() });

      if (result.workflows === 0) {
        toast.success('Nothing to clear — no workflows were left.');
        return;
      }
      const bits = [`${result.workflows} workflow${result.workflows === 1 ? '' : 's'}`];
      if (result.approvals > 0) {
        bits.push(`${result.approvals} open approval${result.approvals === 1 ? '' : 's'}`);
      }
      toast.success(`Cleared ${bits.join(' and ')}.`);
    },
    onError: (err) => {
      toastApiError(err, 'Purge failed');
    },
  });
}
